import { createContext, use, useMemo, ReactNode } from "react";
import authService from "../services/authService";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "antd";
import LogoIcon from "../assets/icons/latest_vivo_logo.svg?react";
import Loader from "../components/UI/Loader";
import { UserInfo } from "./user.types";
import { migrateLegacyWorkspaceStorageForUser } from "@/utils/workspacePersistence";

interface UserContextValue {
  userInfo: UserInfo | undefined;
  loading: boolean;
}

/** Exported for optional consumers (e.g. audit stamping in useTrialPlans)
 *  that must degrade gracefully when no provider is mounted; `useUser` stays
 *  the throwing accessor for screens that require a user. */
// eslint-disable-next-line react-refresh/only-export-components
export const UserContext = createContext<UserContextValue | undefined>(
  undefined,
);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const { data: rawData, isLoading } = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => authService.getUserInfo(),

    // enabled: canFetch,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const userInfo = useMemo<UserInfo | undefined>(() => {
    if (!rawData) return undefined;

    const nextUserInfo = {
      ...(rawData as Record<string, unknown>),
      trials: (rawData as Record<string, unknown>).available_trials,
    } as unknown as UserInfo;
    // Workspace consumers read localStorage during render, so migrate legacy
    // keys before mounting descendants rather than in a passive effect.
    migrateLegacyWorkspaceStorageForUser(nextUserInfo);
    return nextUserInfo;
  }, [rawData]);
  const userReturn: UserContextValue = {
    userInfo,
    loading: isLoading,
  };

  return (
    <UserContext value={userReturn}>
      {isLoading ? <LoadingScreen /> : children}
    </UserContext>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => {
  const context = use(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

const LoadingScreen = () => {
  const { Header, Content } = Layout;

  return (
    <Layout className="main-auth-container">
      <Header className="bg-transparent h-[80px]">
        <div className="flex text-white  items-center h-full justify-between mt-[7px]">
          <LogoIcon />
        </div>
      </Header>
      <Content className="content-auth-container px-[70px]  flex flex-col gap-[50px] items-center ">
        <Loader type="white" />
      </Content>
    </Layout>
  );
};
